'use client';

/**
 * Navbar Component - Exactly like Vercel Commerce
 * 3 sections: Logo+Menu | Search | Cart
 */

import { useSettings } from '@/themes/core/hooks';
import { StandardHeader } from './StandardHeader';
import { StoreNavigation01 } from './StoreNavigation01';
import { StoreNavigation02 } from './StoreNavigation02';
import { AnnouncementBar } from './AnnouncementBar';
import { Menu } from '@/types/navigation';
import { usePathname } from 'next/navigation';

interface NavbarProps {
    siteName: string;
    logo?: string | null;
    menu?: { title: string; path: string }[];
    basePath?: string;
    customMenus?: Menu[];
}

export function Navbar({ siteName, logo, menu = [], customMenus = [], basePath = '' }: NavbarProps) {
    const pathname = usePathname();
    const isCheckout = pathname?.includes('/checkout');
    const { settings } = useSettings();
    const header = (settings.header || {}) as any;
    const announcement_bar = (settings.announcement_bar as any) || {};

    // Fallback to header for backward compatibility
    const announcementSettings = announcement_bar.style
        ? announcement_bar
        : (header as any).announcementText ? header : announcement_bar;

    let headerRegistryKey = 'standard_header';
    // Fix: Ensure both 'modern' and generic 'modern_01' map correctly if needed. 
    // Usually the value is 'modern' for StoreNavigation01 based on previous code.
    // If the user selects "Modern Store 01",    let headerRegistryKey = 'standard_header';
    // Fix: Ensure both 'modern' and generic 'modern_01' map correctly if needed. 
    if ((header as any)?.headerType === 'modern') headerRegistryKey = 'store_navigation_01';
    if ((header as any)?.headerType === 'modern_01') headerRegistryKey = 'store_navigation_01'; // Explicit check
    if ((header as any)?.headerType === 'modern_02') headerRegistryKey = 'store_navigation_02'; // Explicit check
    if ((header as any)?.headerType === 'modern_02') headerRegistryKey = 'store_navigation_02';

    const isAnnouncementSticky = announcementSettings.stickyAnnouncement === true;
    const isNavbarSticky = header.stickyNavbar === true;
    const bothSticky = isAnnouncementSticky && isNavbarSticky;

    // Provide a smart wrapper so they stick together without overlapping,
    // or stick independently without being constrained by a static wrapper.
    const Wrapper = ({ children }: { children: React.ReactNode }) => {
        if (bothSticky && !isCheckout) {
            return (
                <div className="sticky top-0 z-40 w-full flex flex-col bg-background shadow-sm">
                    {children}
                </div>
            );
        }
        return <>{children}</>;
    };

    if (headerRegistryKey === 'store_navigation_01') {
        return (
            <Wrapper>
                {!isCheckout && (
                    <AnnouncementBar
                        {...announcementSettings}
                        stickyAnnouncement={bothSticky ? false : announcementSettings.stickyAnnouncement}
                    />
                )}
                <StoreNavigation01
                    siteName={siteName}
                    logo={logo}
                    menu={menu}
                    showAnnouncement={false} // Handled globally
                    announcementText={announcementSettings.announcementText}
                    stickyNavbar={bothSticky ? false : header.stickyNavbar}
                    showSearch={header.showSearch}
                    searchPlaceholder={header.searchPlaceholder}
                    showCart={header.showCart}
                    showWishlist={header.showWishlist}
                    enableCustomerAccounts={header.enableCustomerAccounts}
                    navbar_direction={header.navbar_direction}
                    customMenus={customMenus}
                    basePath={basePath}
                />
            </Wrapper>
        );
    }

    if (headerRegistryKey === 'store_navigation_02') {
        return (
            <Wrapper>
                {!isCheckout && (
                    <AnnouncementBar
                        {...announcementSettings}
                        stickyAnnouncement={bothSticky ? false : announcementSettings.stickyAnnouncement}
                    />
                )}
                <StoreNavigation02
                    logo={logo}
                    siteName={siteName}
                    menu={menu}
                    navbar_direction={header.navbar_direction}
                    showSearch={header.showSearch}
                    searchPlaceholder={header.searchPlaceholder}
                    showCart={header.showCart}
                    showWishlist={header.showWishlist}
                    enableCustomerAccounts={header.enableCustomerAccounts}
                    customMenus={customMenus}
                    basePath={basePath}
                />
            </Wrapper>
        );
    }

    // Default: Standard Header
    return (
        <Wrapper>
            {!isCheckout && (
                <AnnouncementBar
                    {...announcementSettings}
                    stickyAnnouncement={bothSticky ? false : announcementSettings.stickyAnnouncement}
                />
            )}
            <StandardHeader
                siteName={siteName}
                logo={logo}
                menu={menu}
                // Pass settings as props
                showAnnouncement={false} // Handled globally now
                announcementText={announcementSettings.announcementText}
                stickyAnnouncement={announcementSettings.stickyAnnouncement}
                stickyNavbar={bothSticky ? false : header.stickyNavbar}
                transparent={header.transparent}
                showSearch={header.showSearch}
                searchPlaceholder={header.searchPlaceholder}
                enableCustomerAccounts={header.enableCustomerAccounts}
                showCart={header.showCart}
                showWishlist={header.showWishlist}
                navbar_direction={header.navbar_direction}
                customMenus={customMenus}
                basePath={basePath}
            />
        </Wrapper>
    );
}

export default Navbar;
